import fetch from 'node-fetch';
import { captureException } from '@sentry/nextjs';
import { withDataLayer } from '@pb/shared/utilities/with-data-layer';
import {
    getProperty,
    TGetPropertyResponse,
} from '@pb/shared/services/property';
import { TLoggedInUser } from '@pb/services';
import { getAppEnvironmentSettings } from '@pb/apps/offers/services/environment';
import { TBuyerOfferSession } from '@pb/apps/offers/services/buyer-offer-session';
import {
    TBuyerOffer,
    TBuyerOfferResponse,
    TBuyerOfferSubmittedResponse,
} from './offer.types';
import { mapBuyerOfferSessionDataToFinalOffer } from './offer-map';

export async function getBuyerOfferDetails(
    propertyId: number,
    cookies?: string
): Promise<TBuyerOfferResponse> {
    const { apiBaseUrl } = getAppEnvironmentSettings();

    const result = await fetch(
        `${apiBaseUrl}/sales-offers-api/v1/buyerOffer/${propertyId}`,
        {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Cache': 'no-cache',
                'X-Requested-With': 'XMLHttpRequest',
                ...(cookies && { cookie: cookies }),
            },
            // @ts-ignore
            credentials: 'include',
        }
    );

    if (result.status === 401) {
        return {
            status: 401,
        };
    }

    if (result.status === 400) {
        const actionAlreadyPerformedText =
            'You have already performed this action';
        const resultText = await result.text();
        const isActionAlreadyPerformed =
            actionAlreadyPerformedText === resultText;

        if (isActionAlreadyPerformed) {
            return {
                status: result.status,
                isDuplicate: isActionAlreadyPerformed,
            };
        }
    }

    if (result.status >= 400) {
        throw result;
    }

    return {
        buyerOffer: (await result.json()) as TBuyerOffer,
        status: result.status,
    };
}

export async function postBuyerOfferDetails(
    offerSessionData: TBuyerOfferSession,
    cookies?: string,
    userData?: TLoggedInUser
): Promise<TBuyerOfferSubmittedResponse> {
    const { apiBaseUrl } = getAppEnvironmentSettings();

    const offerData = mapBuyerOfferSessionDataToFinalOffer(offerSessionData);

    const result = await fetch(`${apiBaseUrl}/sales-offers-api/v1/buyerOffer`, {
        method: 'POST',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Cache': 'no-cache',
            'X-Requested-With': 'XMLHttpRequest',
            ...(cookies && { cookie: cookies }),
        },
        // @ts-ignore
        credentials: 'include',
        body: JSON.stringify(offerData),
    });

    if (result.status === 401) {
        return {
            offerId: -1,
            status: 401,
        };
    }

    if (result.status >= 400) {
        throw result;
    }

    let propertyData: TGetPropertyResponse | null;

    try {
        propertyData = await getProperty({
            propertyId: offerData.propertyId,
            baseUrl: apiBaseUrl,
            cookies,
        });
    } catch (error) {
        captureException('Error getting property data', {
            extra: { offerData, error },
        });
        propertyData = null;
    }

    let propertyProgressStatus: string | null;
    switch (offerSessionData.buyerAdditionalDetails.saleProgression) {
        case 'NotOnTheMarket':
            propertyProgressStatus = 'Not on the market';
            break;
        case 'OnTheMarket':
            propertyProgressStatus = 'On the market';
            break;
        case 'SSTC':
            propertyProgressStatus = 'Sold Subject to Contract';
            break;
        default:
            propertyProgressStatus = null;
    }

    const postcodeDistrict = propertyData?.postcode
        ? propertyData.postcode.slice(0, -3).trim().toLowerCase()
        : '';

    const offerId = (await result.json()).offerId;

    withDataLayer(dataLayer => {
        dataLayer.push({
            event: 'offerSubmitted',
            flow: {
                id: 'make an offer',
                stage: 'complete',
            },
            journey: 'buy',
            offerId: offerId,
            propertyid: offerSessionData.propertyId,
            price: propertyData?.marketPrice ?? '',
            offer: offerSessionData.offerAmount,
            postcodeDistrict: postcodeDistrict,
            user: {
                id: userData ? userData.id : '',
                publicId: userData ? userData.publicUserId : '',
            },
            sellingProperty:
                offerSessionData.buyerAdditionalDetails.propertyToSell,
            progressionPropertyStatus: propertyProgressStatus,
            mortgageRequired:
                offerSessionData.buyerAdditionalDetails.mortgageRequired,
            mortgageAdviceOptIn:
                offerSessionData.buyerAdditionalDetails.consentedForMAB ??
                false,
        });
    });

    return {
        offerId,
        status: result.status,
    };
}
